/**
 * @typedef {HTMLElement & {
 *   accordionWithKeyboardEventsInstance?: AccordionWithKeyboardEvents
 * }} AccordionElement
 *
 * Augmented HTML element type for accordions.
 * We add a custom property (`accordionWithKeyboardEventsInstance`)
 * to store the initialized class instance and prevent re-initialization.
 */

/**
 * AccordionWithKeyboardEvents
 *
 * Adds keyboard support to accordion panels.
 * When a panel has focus and the user presses Enter or Space, it simulates a mouse click on the panel.
 */
class AccordionWithKeyboardEvents {
    /**
     * @param {HTMLElement} accordion - The root accordion element.
     */
    constructor(accordion) {
        /**
         * The root accordion element.
         * @type {HTMLElement}
         */
        this.accordion = accordion;

        /**
         * A list of accordion panels inside the accordion.
         * @type {HTMLElement[]}
         */
        this.panels = Array.from(this.accordion.querySelectorAll('.accordion__panel'));

        this._bindEvents();
    }

    /**
     * Bind keyboard events to all panels.
     * Supports "Enter" and "Space" keys for toggling panels.
     * @private
     */
    _bindEvents() {
        this.panels.forEach(panel => {
            panel.addEventListener('keydown', (event) => {
                // Check for Enter or Space key
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    panel.click();
                }
            });
        });
    }
}

/**
 * Initialize all accordions on the page with keyboard events.
 */
function initAccordionWithKeyboardEvents() {
    /** @type {NodeListOf<AccordionElement>} */
    const accordions = document.querySelectorAll('.accordion');
    if (!accordions.length) return;

    accordions.forEach((accordion) => {
        if (!accordion.accordionWithKeyboardEventsInstance) {
            accordion.accordionWithKeyboardEventsInstance = new AccordionWithKeyboardEvents(accordion);
        }
    })
}

// Initialize immediately if DOM is already loaded, otherwise wait for DOMContentLoaded
if (document.readyState !== "loading") {
    initAccordionWithKeyboardEvents();
} else {
    document.addEventListener("DOMContentLoaded", initAccordionWithKeyboardEvents);
}
